import { confirmar } from '@/components/ui/confirm-dialog';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquare, Send, Trash2 } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils/format';
import { useToast } from '@/lib/hooks/use-toast';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { quoteService } from '../services/quote-service';
import type { QuoteComment } from '../types/quote.types';

interface CommentsSectionProps {
  quoteId: string;
  /** FP T13b: si puede borrar los comentarios de OTROS. Lo dice el servidor (no el cargo); ausente = no. */
  puedeBorrarDeOtros?: boolean;
}

export function CommentsSection({ quoteId, puedeBorrarDeOtros = false }: CommentsSectionProps) {
  const [comments, setComments] = useState<QuoteComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();
  const { user } = useAuthStore();

  const loadComments = async () => {
    try {
      setIsLoading(true);
      const data = await quoteService.getComments(quoteId);
      setComments(data);
    } catch {
      // silently fail
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadComments();
  }, [quoteId]);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    try {
      setIsSending(true);
      const comment = await quoteService.createComment(quoteId, newComment.trim());
      setComments((prev) => [...prev, comment]);
      setNewComment('');
    } catch {
      toast({ title: 'Error al enviar comentario', variant: 'destructive' });
    } finally {
      setIsSending(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (
      !(await confirmar({
        title: '¿Eliminar este comentario?',
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    )
      return;
    try {
      await quoteService.deleteComment(quoteId, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      toast({ title: 'Comentario eliminado', variant: 'success' });
    } catch {
      toast({ title: 'Error al eliminar comentario', variant: 'destructive' });
    }
  };

  const canDelete = (comment: QuoteComment) => {
    if (!user) return false;
    return comment.createdById === user.id || puedeBorrarDeOtros;
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Comentarios
          {comments.length > 0 && (
            <span className="text-sm font-normal text-muted-foreground">({comments.length})</span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Comments list */}
        {isLoading ? (
          <div className="text-center py-4 text-muted-foreground text-sm">
            Cargando comentarios...
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground text-sm">
            No hay comentarios aún. Sé el primero en comentar.
          </div>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => (
              <div key={comment.id} className="flex gap-3">
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                  {getInitials(comment.createdBy.firstName, comment.createdBy.lastName)}
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">
                      {comment.createdBy.firstName} {comment.createdBy.lastName}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(comment.createdAt)}
                    </span>
                    {canDelete(comment) && (
                      <button
                        onClick={() => handleDelete(comment.id)}
                        className="ml-auto text-muted-foreground hover:text-destructive p-1 rounded"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{comment.content}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New comment input */}
        <div className="flex gap-2 pt-2 border-t">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Escribe un comentario..."
            className="flex-1 min-h-[60px] max-h-[120px] rounded-md border bg-transparent px-3 py-2 text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                handleSubmit();
              }
            }}
          />
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!newComment.trim() || isSending}
            className="self-end"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
